'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ConnectorName, useWallet } from 'wallet'
import { usePrivy } from '@privy-io/react-auth'
import { openModal } from '@locmod/modal'
import {
  createCustomer,
  getSignedRulesStatus,
  getUserAccountApi,
} from 'src/utils/api'
import { type UserAccountData } from 'src/utils/helper'
import { useUserContext } from 'src/contexts/user'
import { type Address } from 'viem'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'

import { sendGTMEvent } from '@next/third-parties/google'
import { getAuthMethodId } from 'src/utils'
import localStorage from '@locmod/local-storage'
import { constants } from 'helpers'
import { useNudge } from 'src/contexts/nudge'

const FETCHED_USER_INFO_KEY = 'hasFetchedUserInfo'

export const useWalletAuth = () => {
  const router = useRouter()
  const pathname = usePathname()
  const params = useSearchParams()
  const hasFetchedUserInfo = useRef(false)
  const [hasStored, setHasStored] = useState(false)

  const { login: connectOrCreateWallet, user, authenticated } = usePrivy()
  const { loginUser, setLoginCompleted, setUserConnecting } = useUserContext()
  const { openNudge } = useNudge()

  useEffect(() => {
    if (typeof window === 'undefined' || hasStored) {
      return
    }

    const storedInviteCode = localStorage.getItem('inviteCode')
    const storedUtmParams = JSON.parse(
      localStorage.getItem('utmParams') || '{}'
    )

    const inviteCode = storedInviteCode || params.get('invite') // Use stored if available
    const utmParams = {
      utm_source: params.get('utm_source') || storedUtmParams.utm_source || '',
      utm_medium: params.get('utm_medium') || storedUtmParams.utm_medium || '',
      utm_campaign:
        params.get('utm_campaign') || storedUtmParams.utm_campaign || '',
      utm_term: params.get('utm_term') || storedUtmParams.utm_term || '',
      utm_content:
        params.get('utm_content') || storedUtmParams.utm_content || '',
    }

    if (
      !storedInviteCode ||
      Object.values(utmParams).some(value => value !== '')
    ) {
      localStorage.setItem('inviteCode', inviteCode)
      localStorage.setItem('utmParams', JSON.stringify(utmParams))
      setHasStored(true)
    }
  }, [params, hasStored])

  const storedInviteCode = localStorage.getItem('inviteCode')
  const storedUtmParams = JSON.parse(localStorage.getItem('utmParams') || '{}')

  const {
    account,
    isReconnecting,
    isConnecting,
    isConnected,
    isAAWallet,
    connector,
  } = useWallet()

  const getUserInfo = useCallback(
    async (callback?: (userData: UserAccountData) => void) => {
      // Check localStorage for fetch status
      const hasFetchedPreviously =
        localStorage.getItem(FETCHED_USER_INFO_KEY) === 'true'

      if (hasFetchedPreviously) {
        return
      }

      const walletAddress = isAAWallet
        ? user?.smartWallet?.address
        : user?.wallet?.address

      if (!walletAddress) {
        console.warn('Wallet address is missing.')
        return
      }

      setUserConnecting()

      try {
        const status = await getSignedRulesStatus(walletAddress as Address)

        const reqObj = Object.fromEntries(
          Object.entries({
            invite_code: storedInviteCode,
            ...storedUtmParams,
          }).filter(([_, value]) => value)
        )

        const response: UserAccountData = await getUserAccountApi(reqObj)
        if (response) {
          loginUser(response)

          const loginMethod = localStorage
            .getItem(`privy:${constants.privyAppId}:recent-login-method`)
            ?.replace(/^["'](.*)["']$/, '$1')

          if (!status.signed) {
            openModal('SignMessageModal')
          }

          if (!response.is_verified) {
            openNudge({
              message:
                'Click here to link your email & unlock higher benefits. ',
            })
          }

          if (status.signed) {
            setLoginCompleted(true)
            sendGTMEvent({
              event: 'sign_in_completed',
              user_id: response.id,
              login_toolkit: 'privy',
              login_method: (isAAWallet && loginMethod) || 'wallet',
              login_method_id:
                (isAAWallet && getAuthMethodId(user, loginMethod)) ||
                walletAddress,
              is_auto_login: false,
              is_new_user: response.new_account,
              first_name: response.first_name,
              last_name: response.last_name,
              email_address: response.email_id,
              wallet_app_name:
                connector?.name === ConnectorName.Privy
                  ? 'privy'
                  : connector?.name,
              wallet_address: response.wallet_address,
            })

            if (!response.is_verified) {
              openNudge({
                message:
                  'Click here to link your email & unlock higher benefits. ',
              })
            }
          }

          // Store fetch status in localStorage
          localStorage.setItem(FETCHED_USER_INFO_KEY, 'true')
          router.replace(pathname)

          // Execute callback if provided
          if (callback) {
            callback(response)
          }

          return response
        }
      } catch (error) {
        // Remove fetch status on error
        localStorage.removeItem(FETCHED_USER_INFO_KEY)
        throw error
      }
    },
    [
      isAAWallet,
      user,
      setUserConnecting,
      setLoginCompleted,
      loginUser,
      router,
      pathname,
      connector,
      storedInviteCode,
      storedUtmParams,
    ]
  )

  const walletAddress = isAAWallet
    ? user?.smartWallet?.address
    : user?.wallet?.address

  useEffect(() => {
    if (!isConnected) {
      localStorage.removeItem(FETCHED_USER_INFO_KEY)
    }
  }, [isConnected, account])

  useEffect(() => {
    if (
      !localStorage.getItem(FETCHED_USER_INFO_KEY) &&
      authenticated &&
      (isAAWallet ? user?.smartWallet?.address : user?.wallet?.address)
    ) {
      getUserInfo()
    }
  }, [authenticated, walletAddress, hasFetchedUserInfo])

  return {
    account,
    isReconnecting,
    isConnecting,
    isConnected,
    connectOrCreateWallet,
    authenticated,
  }
}
